import { useId } from "react";
import { END, prog, lerp, clamp01, easeOutCubic, easeInOutCubic, easeOutBack } from "./timeline";
import { GRAIN, flamePath, leafPath, Log, Defs, Paper } from "./parts";

export const THEMES = {
  light: { ring: "#2F6B2E", dark: "#1E4A26", green: "#5B8F2E", tag: "#1E4A26", gap: "#F4F1E9", paper: "#F4F1E9", noise: 0.07, vig: "#3C321E" },
  dark: { ring: "#3F8B3B", dark: "#F3F1EA", green: "#8DC24C", tag: "#A9C2B8", gap: "#F4F1E9", paper: "#0D1F1A", noise: 0.04, vig: "#000000" },
};

const LOGS = [
  { x: -122, y: 72, rot: -54, len: 178, r: 22, s: 1.1 },
  { x: -66, y: 100, rot: -62, len: 160, r: 21, s: 1.32 },
  { x: 36, y: 138, rot: 193, len: 166, r: 22, s: 1.55 },
];
const TX = 640;
const WORD = "KASHYAP".split("");
const FONT = "Montserrat, sans-serif";
const L1 = { x: TX, y: 410, fontFamily: FONT, fontWeight: 800, fontSize: 150 };
const L2 = { x: TX, y: 530, fontFamily: FONT, fontWeight: 700, fontSize: 110 };
const L3 = { x: TX, y: 600, fontFamily: FONT, fontWeight: 500, fontSize: 44 };

export default function LogoSVG({ t = END, clock = 0, theme = "light", bg = "none", flicker = true, fontCss = "", idPrefix, width, height, className, bgOnly = false, viewBox = "0 0 1600 900", emblemOnly = false }) {
  const rid = useId();
  const id = (idPrefix || `k${rid}`).replace(/[^a-zA-Z0-9_-]/g, "");
  const c = THEMES[theme];
  const C = 2 * Math.PI * 172;
  const ringP = easeInOutCubic(prog(t, 0, 0.95));
  const emS = lerp(0.94, 1, easeOutCubic(prog(t, 0, 1.4)));
  const diskP = easeOutCubic(prog(t, 0.45, 0.55));
  const flameP = prog(t, 1.9, 0.7);
  const amp = t >= END ? (flicker ? 2.5 : 0) : 6;
  const k = amp * (Math.sin(clock * 7.1) + 0.5 * Math.sin(clock * 13.7));
  const sy = 1 + (amp / 6) * 0.03 * Math.sin(clock * 9.3);
  const leafP = prog(t, 2.2, 0.75);
  const g2 = easeOutCubic(prog(t, 3.15, 0.65));
  const g3 = easeOutCubic(prog(t, 3.6, 0.8));
  const sw = prog(t, 4.2, 0.85);

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} width={width} height={height} className={className} role="img" aria-label="Kashyap Green Fuels — Sustainable Energy">
      <defs>
        {fontCss && <style>{fontCss}</style>}
        <Defs id={id} c={c} />
        <clipPath id={`${id}-l2`}>
          <rect x={TX - 10} y="420" width={20 + 900 * g2} height="140" />
        </clipPath>
        <clipPath id={`${id}-shine`}>
          <circle cx="400" cy="450" r="186" />
          <text {...L1}>KASHYAP</text>
          <text {...L2}>GREEN FUELS</text>
          <text {...L3} letterSpacing="13">SUSTAINABLE ENERGY</text>
        </clipPath>
      </defs>
      {(bg === "paper" || bgOnly) && <Paper id={id} c={c} />}
      {!bgOnly && (
        <>
          <g transform={`translate(400 450) scale(${emS})`}>
            <circle r="162" fill={c.gap} opacity={ringP > 0 ? 1 : 0} />
            <g clipPath={`url(#${id}-inner)`}>
              <g transform={`translate(-30 -28) scale(${lerp(0.5, 1, diskP)})`} opacity={diskP}>
                <circle r="134" fill={`url(#${id}-wood)`} stroke="#8A6236" strokeWidth="10" />
                {GRAIN.map((d, i) => {
                  const p = easeOutCubic(prog(t, 0.55 + i * 0.08, 0.5));
                  return <path key={i} d={d} fill="none" stroke="#B88E58" strokeWidth={i % 2 ? 2 : 3} opacity={p * 0.85} transform={`scale(${lerp(0.3, 1, p)})`} />;
                })}
                <circle r="7" fill="#B88E58" opacity={diskP} />
                <path d="M-120 -40 L-92 -30 L-84 -14 L-62 -10" fill="none" stroke="#8A6236" strokeWidth="3.5" strokeLinecap="round" opacity={easeOutCubic(prog(t, 1.0, 0.4))} />
              </g>
            </g>
            <circle r="172" fill="none" stroke={c.ring} strokeWidth="24" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - ringP)} transform="rotate(-90)" opacity={ringP > 0.001 ? 1 : 0} />
            <g transform={`translate(78 34) scale(${easeOutBack(flameP)}) scale(1 ${sy})`}>
              <ellipse cx="20" cy="-110" rx="120" ry="150" fill={`url(#${id}-glow)`} opacity={flameP * 0.55} />
              <path d={flamePath(64, 262, k)} fill={`url(#${id}-flame)`} stroke={c.gap} strokeWidth="5" paintOrder="stroke" />
              <path d={flamePath(34, 160, k * 0.7)} transform="translate(14 -8)" fill="#F7E3A6" opacity="0.8" />
            </g>
            <g clipPath={`url(#${id}-inner)`}>
              {LOGS.map((l, i) => {
                const p = prog(t, l.s, 0.6);
                const dy = -340 * (1 - easeOutBack(p, 1.2));
                return (
                  <g key={i} transform={`translate(0 ${dy})`} opacity={clamp01(p * 4)}>
                    <g transform={`translate(${l.x} ${l.y}) rotate(${l.rot})`}>
                      <Log len={l.len} r={l.r} gid={id} outline={c.gap} />
                    </g>
                  </g>
                );
              })}
            </g>
            <g transform={`translate(8 166) rotate(${-45 + (1 - easeOutCubic(leafP)) * 25}) scale(${easeOutBack(leafP)})`}>
              <path d={leafPath(252, 74)} fill={`url(#${id}-leaf)`} stroke={c.gap} strokeWidth="7" paintOrder="stroke" />
              <path d="M6 0 Q 126 -14 236 -1" fill="none" stroke="#CFE6A0" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
              <path d="M70 -8 Q 92 -34 110 -52 M120 -10 Q 142 -32 160 -46 M100 -6 Q 118 18 128 32 M160 -6 Q 176 12 186 22" fill="none" stroke="#CFE6A0" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
            </g>
          </g>

          {!emblemOnly && <>
          <text {...L1} fill={c.dark}>
            {WORD.map((ch, i) => {
              const p = easeOutCubic(prog(t, 2.6 + i * 0.07, 0.45));
              return <tspan key={i} y={L1.y + 46 * (1 - p)} fillOpacity={p}>{ch}</tspan>;
            })}
          </text>
          <g clipPath={`url(#${id}-l2)`}>
            <text {...L2} fill={c.green} opacity={g2} transform={`translate(${-80 * (1 - g2)} 0)`}>GREEN FUELS</text>
          </g>
          <text {...L3} fill={c.tag} opacity={g3} letterSpacing={lerp(34, 13, g3)}>SUSTAINABLE ENERGY</text>

          {sw > 0 && sw < 1 && (
            <g clipPath={`url(#${id}-shine)`}>
              <rect x={lerp(-400, 1600, easeInOutCubic(sw))} y="200" width="320" height="500" fill={`url(#${id}-sweep)`} transform="skewX(-18)" />
            </g>
          )}
          </>}
        </>
      )}
    </svg>
  );
}
